import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import {
    SelfImprovement as YogaIcon,
    Chat as ChatIcon,
    GraphicEq as SoundTherapyIcon,
    BarChart as BarChartIcon,
    AccessTime as TimeIcon,
    ChevronRight as ChevronRightIcon,
    MonitorHeart as HeartIcon,
    Timer as TimerIcon,
    EmojiEvents as TrophyIcon,
    Speed as SpeedIcon,
    TrendingUp as TrendingUpIcon,
    Nightlight as MoonIcon,
    GpsFixed as TargetIcon,
} from '@mui/icons-material';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    PieChart,
    Pie,
    Cell,
    ComposedChart,
    Line,
} from 'recharts';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FavoriteIcon from '@mui/icons-material/Favorite';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';

import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getAnalyticsData } from '../../service/insights';


// ── Inline SVG sparkline ──
const Sparkline = ({ data, color }) => {
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const W = 72, H = 28, P = 2;
    const pts = data.map((v, i) => {
        const x = P + (i / (data.length - 1)) * (W - P * 2);
        const y = H - P - ((v - min) / range) * (H - P * 2);
        return `${x},${y}`;
    }).join(' ');
    return (
        <svg width={W} height={H} style={{ overflow: 'visible', display: 'block' }}>
            <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.75" />
        </svg>
    );
};

// ── Custom recharts tooltip ──
const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', p: 1.5, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
            <Typography sx={{ fontWeight: 700, color: '#0B2D6B', fontSize: '0.78rem', mb: 0.5 }}>{label}</Typography>
            {payload.map((p, i) => (
                <Typography key={i} sx={{ fontSize: '0.73rem', color: p.color, fontWeight: 600 }}>
                   {p.name === 'minutes'
    ? `Time: ${formatDuration(p.value * 60)}`
    : `Sessions: ${p.value}`
}
                </Typography>
            ))}
        </Box>
    );
};

const activityMap = {
    page_view: {
        name: "Page View",Icon: VisibilityIcon,color: "#6366f1", bg: "#eef2ff",
    },

    care: {
        name: "Care",Icon: FavoriteIcon,color: "#22c55e", bg: "#dcfce7",
    },

    podcast: {
        name: "Podcast",Icon: GraphicEqIcon,color: "#f59e0b", bg: "#fef3c7",
    },

    converse: {
        name: "Converse",Icon: ChatIcon,color: "#ec4899", bg: "#fce7f3",
    },

    nurture_within: {
        name: "Nurture Within",Icon: SelfImprovementIcon,color: "#8b5cf6", bg: "#f5f3ff",
    },

    sound_therapy: {
        name: "Sound Therapy",Icon: MusicNoteIcon,color: "#14b8a6", bg: "#ccfbf1",
    },
};

const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }

    return `${minutes}m`;
};

const formatEventName = (eventName) => {
    if (eventName === 'page_view') return 'Home';

    return eventName
        ?.replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
};
// ── Main component ──
const ActivityAnalyticsTimeline = ({ timeFilter = 'weekly' }) => {
    const [analyticsData, setAnalyticsData] = React.useState(null);
  const trendData = analyticsData?.activity_trend || [];

   const breakdownData =
    analyticsData?.activity_breakdown?.map(item => {

        const config = activityMap[item.event_name] || {
            name: item.event_name,
            Icon: AutoAwesomeIcon,
            color: "#64748b",
            bg: "#f8fafc",
        };

        return {
            ...item,
            ...config,
 name: formatEventName(item.event_name),        };
    }) || [];
    const totalBreakdownDuration = breakdownData.reduce(
    (sum, item) => sum + item.duration,
    0
);

const insightsList = analyticsData?.insights || [];
 const topActivities =
    analyticsData?.top_activities?.map(item => {

        const config = activityMap[item.event_name] || {
            name: item.event_name,
            Icon: AutoAwesomeIcon,
            color: "#64748b",
            bg: "#f8fafc",
        };

        return {
            ...item,
            ...config,
 name: formatEventName(item.event_name),        };
    }) || [];
    
    
    const statCards = analyticsData
    ? [
          {
              label: 'Total Sessions',
              value: analyticsData.stats.total_sessions,
              trend: 'Sessions',
              up: true,
              color: '#22c55e',
              iconBg: '#dcfce7',
              Icon: HeartIcon,
              spark: [1, 2, 3, 2, 4, 3, 2],
          },
          
         {
         label: 'Total Time',
          value: formatDuration(
          analyticsData?.usage_seconds?.[timeFilter] || 0
           ),
           trend: 'Usage Time',
           up: true,
           color: '#14b8a6',
           iconBg: '#ccfbf1',
           Icon: TimerIcon,
           spark: [1, 2, 3, 4, 3, 2, 1],
},
          {
              label: 'Active Days',
              value: analyticsData.stats.active_days,
              trend: 'Days Active',
              up: true,
              color: '#8b5cf6',
              iconBg: '#f5f3ff',
              Icon: TrophyIcon,
              spark: [1, 1, 1, 1, 1, 1, 1],
          },
          {
              label: 'Most Used Activity',
              value:
            analyticsData.stats.most_used_activity?.event_name === 'page_view'
          ? 'Home'
             : activityMap[
              analyticsData.stats.most_used_activity?.event_name
          ]?.name || '-',
              trend: 'Top Activity',
              up: true,
              color: '#6366f1',
              iconBg: '#eef2ff',
              Icon: SpeedIcon,
              spark: [1, 2, 3, 2, 3, 4, 3],
          },
      ]
    : [];
    React.useEffect(() => {

    const fetchAnalytics = async () => {
        const userId = localStorage.getItem("user_id");
        console.trace("fetchAnalytics called");

        try {


            const response = await getAnalyticsData(
                userId,
                timeFilter
            );

            console.log(response);

            setAnalyticsData(response);

        } catch (error) {

            console.error(error);
        }
    };

    fetchAnalytics();

}, [timeFilter]);


console.log("analyticsData", analyticsData);
    return (
        <Box>

            {/* ── Header ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <BarChartIcon sx={{ color: '#fff', fontSize: 24 }} />
                </Box>
                <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Activity Analytics</Typography>
                    <Typography sx={{ color: '#64748b', fontSize: '0.85rem' }}>Your journey to wellness, beautifully tracked ✨</Typography>
                </Box>
            </Box>

            {/* ── Stat Cards ── */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {statCards.map((card, i) => (
                    <Grid item xs={6} sm={3} key={i}>
<Box
    sx={{
        p: 2,
        borderRadius: '16px',
        background:'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
        backgroundSize: '200% 200%',
        border: '1.5px solid #f0e4ff',
        boxShadow: '0 4px 24px rgba(180,140,255,0.12)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        animation: 'gradientShift 8s ease infinite, fadeIn 0.6s ease-out',
        animationDelay: `${i * 0.1}s`,
        opacity: 0,
        animationFillMode: 'forwards',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 32px rgba(180,140,255,0.25)',
        },
        '@keyframes gradientShift': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
        },
        '@keyframes fadeIn': {
            '0%': { opacity: 0, transform: 'translateY(20px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
        },
    }}
>                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: card.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <card.Icon sx={{ fontSize: 22, color: card.color }} />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.8)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                        {card.label}
                                    </Typography>
                                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 900, color: '#fff', lineHeight: 1.2 }}>
                                        {card.value}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            {/* ── Middle Row: Trend + Breakdown ── */}
            <Grid container spacing={2} sx={{ mb: 3 }}>

                {/* Activity Trend */}
                <Grid item xs={12} md={7}>
                    <Box sx={{ p: 2.5, borderRadius: '16px', background: '#fff', border: '1.5px solid #f1f5f9', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#0B2D6B' }}>Activity Trend</Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, textTransform: 'capitalize' }}>
                                {timeFilter === 'daily' ? 'Today' : timeFilter === 'weekly' ? 'This Week' : 'This Month'}
                            </Typography>
                        </Box>

                        {/* Legend */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                <Box sx={{ width: 14, height: 3, borderRadius: 2, background: '#8b5cf6' }} />
                                <Typography sx={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>Session Time (min)</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '3px', background: '#22c55e', opacity: 0.75 }} />
                                <Typography sx={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>Sessions</Typography>
                            </Box>
                        </Box>
<Box
    sx={{
        display: "flex",
        justifyContent: "space-between",
        mb: 1,
    }}
>
    <Typography
        sx={{
            fontSize: "0.75rem",
            color: "#64748b",
            fontWeight: 600,
        }}
    >
        {timeFilter === "monthly" ? "Hours" : "Minutes"}
    </Typography>

    <Typography
        sx={{
            fontSize: "0.75rem",
            color: "#64748b",
            fontWeight: 600,
        }}
    >
        Sessions
    </Typography>
</Box>
                        <ResponsiveContainer width="100%" height={200}>
  <ComposedChart data={trendData} margin={{ top: 5, right: 14, left: -22, bottom: 0 }}>                                <CartesianGrid vertical={false} stroke="#f1f5f9" />
                                <XAxis
                                    dataKey="label"
                                    hide={timeFilter === "daily"}
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#94a3b8', fontSize: 10 }}
                                /><YAxis
           yAxisId="left" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }}tickFormatter={(value) =>value >= 60 ? `${Math.round(value / 60)}h` : `${value}m`}/>                               
               <YAxis yAxisId="right" orientation="right"axisLine={false} tickLine={false}  tick={{ fill: '#94a3b8', fontSize: 10 }}allowDecimals={false}/>                                <Tooltip content={<CustomTooltip />} />
                                <Bar yAxisId="left"  dataKey="minutes"  fill="#22c55e" radius={[6,6,0,0]} barSize={20} opacity={0.7} name="minutes" />
                               <Line yAxisId="right" type="monotone" dataKey="sessions" stroke="#8b5cf6" strokeWidth={2.5} dot={false} activeDot={false} name="sessions" />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </Box>
                </Grid>

                {/* Activity Breakdown */}
                <Grid item xs={12} md={5}>
                    <Box sx={{ p: 2.5, borderRadius: '16px', background: '#fff', border: '1.5px solid #f1f5f9', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.1 }}>
                            <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#0B2D6B' }}>Activity Breakdown</Typography>
                        </Box>
<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
                            {/* Donut */}
                            <Box sx={{ position: 'relative', width: 120, height: 120, flexShrink: 0 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={breakdownData} cx="50%" cy="50%" innerRadius={36} outerRadius={55} paddingAngle={3} dataKey="duration" startAngle={90} endAngle={-270}>
                                            {breakdownData.map((entry, index) => (
                                                <Cell key={index} fill={entry.color} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center', pointerEvents: 'none' }}>
                                    <Typography sx={{ fontWeight: 900, fontSize: '1.2rem', color: '#0B2D6B', lineHeight: 1 }}>{analyticsData?.stats?.total_sessions || 0}</Typography>
                                    <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 600 }}>Total</Typography>
                                </Box>
                            </Box>

                            {/* Legend list */}
                            <Box sx={{ 
                                flex: 1,
                                maxHeight: '200px',
                                overflowY: 'auto',
                                pr: 0.5,
                                '&::-webkit-scrollbar': { width: '6px' },
                                '&::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.05)', borderRadius: '10px' },
                                '&::-webkit-scrollbar-thumb': { background: 'rgba(99,102,241,0.3)', borderRadius: '10px' },
                                '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(99,102,241,0.5)' }
                            }}>
                                {breakdownData.map((item, i) => (
                                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: i < breakdownData.length - 1 ? 1.4 : 0 }}>
                                       <Box
    sx={{
        width: 28, height: 28,  borderRadius: '8px',  background: item.bg, display: 'flex',alignItems: 'center',justifyContent: 'center',}}>
    <item.Icon
        sx={{
            fontSize: 14,
            color: item.color,
        }}
    />
</Box>
                                       <Box
    sx={{
        flex: 1, display: 'flex',  justifyContent: 'space-between', alignItems: 'center', }}>
    <Typography
        sx={{ fontSize: '0.8rem', fontWeight: 700,color: '#0B2D6B', }}>
        {item.name}
    </Typography>

    <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>
        {Math.round(
            (item.duration / totalBreakdownDuration) * 100
        )}% ({formatDuration(item.duration)})
    </Typography>
</Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* ── Bottom Row: Recent Sessions + Insights ── */}
            <Grid container spacing={2}>

                {/* Recent Sessions */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2.5, borderRadius: '16px', background: '#fff', border: '1.5px solid #f1f5f9', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#0B2D6B' }}>Most Used Activity</Typography>
                        </Box>
                        <Box sx={{
                            maxHeight: '210px',
                            overflowY: 'auto',
                            pr: 0.5,
                            '&::-webkit-scrollbar': { width: '6px' },
                            '&::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.05)', borderRadius: '10px' },
                            '&::-webkit-scrollbar-thumb': { background: 'rgba(99,102,241,0.3)', borderRadius: '10px' },
                            '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(99,102,241,0.5)' }
                        }}>
                            {topActivities.map((s, i) => (
                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.3, borderTop: i > 0 ? '1px solid #f8fafc' : 'none' }}>
                                    <Box sx={{ width: 48, height: 48, borderRadius: '11px', background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <s.Icon sx={{ fontSize: 20, color: s.color }} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontWeight: 700, color: '#0B2D6B', fontSize: '0.88rem' }}>{s.name}</Typography>
                                        <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                                            Today • {s.session_count || 1} Sessions
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                        <Box sx={{ px: 1.2, py: 0.4, borderRadius: '20px', background: s.bg, border: 'none', display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                            <TimeIcon sx={{ fontSize: 11, color: s.color }} />
                                            <Typography sx={{ fontSize: '0.8rem', color: '#475569', fontWeight: 700 }}>{formatDuration(s.total_duration)}</Typography>
                                        </Box>
                                        <ChevronRightIcon sx={{ color: '#cbd5e1', fontSize: 18 }} />
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Grid>

                {/* Insights for You */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ borderRadius: '22px', border: '1.5px solid #f0e4ff', boxShadow: '0 4px 24px rgba(180,140,255,0.12)', height: '100%', overflow: 'hidden', position: 'relative' }}>

                        {/* ── SVG background ── */}
                        <Box component="svg" viewBox="0 0 800 320" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
                            <defs>
                                <linearGradient id="ins_bg" x1="0" y1="0" x2="1" y2="1">
                                    <stop offset="0%" stopColor="#FFF7FD"/>
                                    <stop offset="50%" stopColor="#F8F2FF"/>
                                    <stop offset="100%" stopColor="#F3F9FF"/>
                                </linearGradient>
                                <linearGradient id="ins_wave" x1="0" y1="0" x2="1" y2="0">
                                    <stop offset="0%" stopColor="#E9FFF3"/>
                                    <stop offset="100%" stopColor="#EAF7FF"/>
                                </linearGradient>
                                <radialGradient id="ins_g1" cx="50%" cy="50%" r="50%">
                                    <stop offset="0%" stopColor="#FFE8F5" stopOpacity="0.9"/>
                                    <stop offset="100%" stopColor="#FFE8F5" stopOpacity="0"/>
                                </radialGradient>
                                <radialGradient id="ins_g2" cx="50%" cy="50%" r="50%">
                                    <stop offset="0%" stopColor="#FFF8CC" stopOpacity="0.8"/>
                                    <stop offset="100%" stopColor="#FFF8CC" stopOpacity="0"/>
                                </radialGradient>
                                <filter id="ins_blur">
                                    <feGaussianBlur stdDeviation="30"/>
                                </filter>
                            </defs>
                            <rect width="800" height="320" rx="0" fill="url(#ins_bg)"/>
                            <circle cx="680" cy="70" r="90" fill="url(#ins_g1)" filter="url(#ins_blur)"/>
                            <circle cx="740" cy="40" r="60" fill="url(#ins_g2)" filter="url(#ins_blur)"/>
                            <path d="M0 250 C120 220, 220 300, 340 260 C460 220, 560 300, 800 240 L800 320 L0 320 Z" fill="url(#ins_wave)" opacity="0.6"/>
                            <path d="M0 280 C150 240, 260 330, 420 280 C580 230, 690 320, 800 290 L800 320 L0 320 Z" fill="#FFFFFF" opacity="0.4"/>
                            <g opacity="0.8">
                                <circle cx="620" cy="50" r="3" fill="#FFD6F5"/>
                                <circle cx="700" cy="90" r="4" fill="#FFF1A8"/>
                                <circle cx="640" cy="120" r="2" fill="#E7C8FF"/>
                                <circle cx="740" cy="140" r="3" fill="#FFD6F5"/>
                            </g>
                        </Box>

                        {/* ── Content (above SVG) ── */}
                     <Box
    sx={{
        position: 'relative',
        zIndex: 1,
        p: 2.5,
        display: 'flex',
        justifyContent: 'space-between',
    }}
>

                            {/* Left: title + items */}
                           <Box sx={{ flex: 0.65, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                                    <Typography sx={{ fontSize: '1.1rem', lineHeight: 1 }}>✨</Typography>
                                    <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#0B2D6B' }}>Insights for You</Typography>
                                </Box>
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: 2.5,
                                    maxHeight: '210px',
                                    overflowY: 'auto',
                                    pr: 1,
                                    '&::-webkit-scrollbar': { width: '6px' },
                                    '&::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.05)', borderRadius: '10px' },
                                    '&::-webkit-scrollbar-thumb': { background: 'rgba(139,92,246,0.3)', borderRadius: '10px' },
                                    '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(139,92,246,0.5)' }
                                }}>
                                    {insightsList.map((item, i) => (
                                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{ width: 42, height: 42, borderRadius: '12px', background: item.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                               <Typography>✨</Typography>
                                            </Box>
                                            <Box>
                                                <Typography sx={{ fontWeight: 800, color: '#0B2D6B', fontSize: '0.88rem', mb: 0.2 }}>{item.title}</Typography>
                                                <Typography sx={{ color: '#64748b', fontSize: '0.74rem', lineHeight: 1.4 }}>{item.description}</Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>

                            {/* Right: illustration */}
                            <Box sx={{ width: 140, flexShrink: 0, display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center', ml: 2 }}>
                                <Typography sx={{ fontSize: '7rem', lineHeight: 1, filter: 'drop-shadow(0 4px 16px rgba(139,92,246,0.25))' }}>🧘‍♀️</Typography>
                            </Box>

                        </Box>
                    </Box>
                </Grid>
            </Grid>

        </Box>
    );
};

export default ActivityAnalyticsTimeline;
